import { filter, switchMap, catchError } from 'rxjs/operators'
import { of } from 'rxjs'

import { ChangeDetectorRef, Component, OnInit } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { NavigationEnd, Router } from '@angular/router'
import { AdminService } from '@services/admin.service'
import { PermissionService } from '@services/permission.service'
import { AuthenticationService } from '@services/authentication.service'
import { EnvironmentManagerService } from '@services/environment-manager.service'
import { SidebarService } from '@services/sidebar.service'
import { SubscriptionService } from '@services/subscription.service'
import { UserService } from '@services/user.service'

import { APP_ADMIN_NAV_ITEM, APP_NAV_ITEMS, NavItem } from '../app-navigation'
import { SearchModalComponent } from '../modals/search-modal/search-modal.component'

@Component({
    selector: 'shared-app-sidebar',
    templateUrl: './app-sidebar.component.html',
    styleUrls: ['./app-sidebar.component.css'],
  standalone: false
})
export class AppSidebarComponent implements OnInit {
    get currentModule() { return this._authenticationService.currentModule }
    get hasUserAdminAccess() { return this._authenticationService.hasUserAdminAccess }
    get inFreeTier() { return this._subscriptionService.inFreeTier }
    get isLegacy() { return this._subscriptionService.isLegacy }
    get currentUser$() { return this._authenticationService.user$ }
    get collapsed$() { return this._sidebarService.collapsed$ }
    get isMobile() { return this._environmentManagerService.isMobile }
    get isAssetPage() { return this.currentModule === 'assets' }
    /** Check if current user is a Kartorium super admin (Kartorium staff only, distinct from client "super user" role) */
    get isSuperAdmin() { return this._userService.currentUser?.superAdmin ?? false }

    /** Track if we're on the admin page */
    isOnAdminPage = false

    adminNavItem = APP_ADMIN_NAV_ITEM
    navItems: NavItem[] = APP_NAV_ITEMS

    constructor(
        private _adminService: AdminService,
        private _permissionService: PermissionService,
        private _authenticationService: AuthenticationService,
        private _cdr: ChangeDetectorRef,
        private _environmentManagerService: EnvironmentManagerService,
        private _router: Router,
        private _subscriptionService: SubscriptionService,
        private _sidebarService: SidebarService,
        private _userService: UserService,
        private _dialog: MatDialog
    ) { }

    ngOnInit(): void {
        // Load organization settings and user roles after authentication
        this._authenticationService.isAuthenticated$.pipe(
            filter(isAuth => isAuth),
            switchMap(() => this._adminService.getCurrentOrgSettings().pipe(
                catchError(() => of(null)) // Silently handle errors
            )),
            switchMap(() => this._authenticationService.getCurrentUsersRoles$().pipe(
                catchError(() => of([])) // Silently handle errors
            )),
            switchMap(() => this._permissionService.refresh().pipe(
                catchError(() => of(null))
            ))
        ).subscribe()

        // React to org settings changes (e.g., when admin updates settings)
        this._adminService.currentOrgSettings$.subscribe(() => {
            this._cdr.detectChanges()
        })

        // React to tier changes
        this._permissionService.roles$.subscribe(() => {
            this._cdr.detectChanges()
        })

        // Track if on admin page
        this.isOnAdminPage = this._router.url.startsWith('/admin')
        this._router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe((event: NavigationEnd) => {
            this.isOnAdminPage = event.urlAfterRedirects.startsWith('/admin')
            this._cdr.detectChanges()
        })
    }

    toggleSidebar(): void {
        this._sidebarService.toggle()
    }

    navigate(item: NavItem): void {
        if (this.isNavItemDisabled(item)) {
            return
        }
        // Collapse sidebar on navigation
        this._sidebarService.collapse()
        this._router.navigate([item.route])
    }

    isNavItemActive(item: NavItem): boolean {
        return this.currentModule === item.module
    }

    isNavItemDisabled(item: NavItem): boolean {
        if (item.disabledInFreeTier && this.inFreeTier) {
            return true
        }
        return false
    }

    shouldShowNavItem(item: NavItem): boolean {
        if (item.hideForLegacy && this.isLegacy) {
            return false
        }
        if (item.hideInFreeTier && this.inFreeTier) {
            return false
        }
        if (item.requiresAdmin && !this.hasUserAdminAccess && !this.inFreeTier && !this.isSuperAdmin) {
            return false
        }
        // Check if module is hidden by organization settings
        // On admin page, show hidden items as muted instead of hiding
        if (this._adminService.isModuleHidden(item.moduleId)) {
            return this.isOnAdminPage
        }
        // Tier-based access (e.g. OEE hidden for operator tier)
        if (!this.isOnAdminPage && !this._permissionService.canAccess(item.module)) {
            return false
        }
        return true
    }

    /** Check if a nav item is hidden by org settings (for muted styling on admin page) */
    isNavItemHiddenByOrgSettings(item: NavItem): boolean {
        return this._adminService.isModuleHidden(item.moduleId)
    }

    /** Check if AI Search feature is visible */
    get showAISearch(): boolean {
        // On admin page, always show (but may be muted)
        if (this.isOnAdminPage) return true
        return !this._adminService.isModuleHidden('ai_search')
    }

    /** Check if AI Search is hidden by org settings */
    get isAISearchHidden(): boolean {
        return this._adminService.isModuleHidden('ai_search')
    }

    /** Show admin link for Kartorium super admins (Kartorium staff only, distinct from client "super user" role) */
    get showAdminLink(): boolean {
        return this.isSuperAdmin
    }

    openSearchModal(): void {
        this._dialog.open(SearchModalComponent, {
            width: '800px',
            maxWidth: '90vw',
            panelClass: 'search-modal-container'
        })
    }
}
